import { v4 as uuidv4 } from 'uuid';

import {
  listMessages,
  bulkCreateMessages,
  mediaAttachmentUploadUrl,
} from '@wix/ambassador-innovation-widget-v1-message/http';
import { getConversation } from '@wix/ambassador-innovation-widget-v1-conversation/http';
import {
  ListMessagesRequest,
  CreateMessageStreamRequest,
  CreateMessageStreamResponse,
  Sender,
  MessageType,
} from '@wix/ambassador-innovation-widget-v1-message/types';
import { FileDescriptor } from '@wix/ambassador-media-site-media-v1-file-descriptor/types';

import {
  senderIsAssistant,
  senderIsOwner,
  senderIsVisitor,
} from '@/utils/sender';
import { EXPERIMENTS } from '@/constants';
import { PanoramaTransactions } from '@/panoramaTransactions';
import type { AppDispatch, RootState, ThunkExtraArgument } from '@/store/store';
import * as WixAssistantWidget from '@/services/wixAssistantWidget';

import { FormData } from 'AiAssistantWidget/Widget/components';

import { createAppAsyncThunk } from '../createAppAsyncThunk';
import { selectLayout } from '../layout';
import { flagsSlice } from '../flags';
import { CurrentStatus } from '../oneChatSettings/model';

import {
  formatMessage,
  formatMessages,
  isAllowedMessage,
  messagesQueryBuilder,
  SPECIAL_MESSAGE_TYPES,
} from './utils';
import {
  getDummyAssistantMessages,
  getDummyBubbleMessages,
  getDummyCardMessages,
  getDummyContactForm,
  getDummyMessages,
  getDummyVisitorMessages,
} from './dummyMessages';
import { messagesSlice } from './messagesSlice';
import {
  AssistantAction,
  formSubmittedAdditionalData,
  hasAssistantActions,
  Message,
  WidgetApi,
} from './model';
import {
  ConversationChannel,
  createConversationChannel,
  DuplexerMergeConversationsMessage,
  DuplexerMessage,
  isMessageTypingEvent,
  isNewMessageEvent,
} from './conversationChannel';

import { selectMessages } from '.';

/** Clears duplexer typing indicator if no follow-up message (handles stale typing after last assistant chunk). */
let typingIndicatorExpiryTimer: ReturnType<typeof setTimeout> | null = null;

function clearTypingIndicatorExpiryTimer() {
  if (typingIndicatorExpiryTimer) {
    clearTimeout(typingIndicatorExpiryTimer);
    typingIndicatorExpiryTimer = null;
  }
}

function shouldTrackAwaitingAnswer(getState: () => RootState) {
  return (
    !getState().messages.manualInterventionActive &&
    getState().oneChatSettings.currentStatus ===
      CurrentStatus.AI_ASSISTANT_ONLINE
  );
}

function isAwaitingAnswer(getState: () => RootState) {
  return (
    shouldTrackAwaitingAnswer(getState) && getState().messages.awaitingAnswer
  );
}

function startTypingIndicator(dispatch: AppDispatch) {
  clearTypingIndicatorExpiryTimer();
  dispatch(messagesSlice.actions.setIsTyping(true));
}

function startAwaitingAnswer(dispatch: AppDispatch, getState: () => RootState) {
  if (!shouldTrackAwaitingAnswer(getState)) {
    return;
  }
  dispatch(messagesSlice.actions.setAwaitingAnswer(true));
  startTypingIndicator(dispatch);
}

function stopTypingIndicator(dispatch: AppDispatch) {
  clearTypingIndicatorExpiryTimer();
  dispatch(messagesSlice.actions.setIsTyping(false));
}

function stopAwaitingAnswer(dispatch: AppDispatch) {
  dispatch(messagesSlice.actions.setAwaitingAnswer(false));
  stopTypingIndicator(dispatch);
}

function isOwnerInterventionNotification(event: DuplexerMessage) {
  return (
    isNewMessageEvent(event) &&
    event.sender === Sender.SYSTEM &&
    event.messageType === MessageType.SITE_OWNER_NOTIFICATION
  );
}

function shouldResumeAiTypingAfterManualIntervention(
  event: DuplexerMessage,
  getState: () => RootState,
) {
  if (
    !isMessageTypingEvent(event) ||
    !getState().messages.manualInterventionActive ||
    getState().oneChatSettings.currentStatus !==
      CurrentStatus.AI_ASSISTANT_ONLINE ||
    !event.answerTo
  ) {
    return false;
  }

  const answeredMessage = selectMessages.entities(getState())[event.answerTo];

  return senderIsVisitor(answeredMessage?.sender);
}

function startManualIntervention(dispatch: AppDispatch) {
  dispatch(messagesSlice.actions.setManualInterventionPending(false));
  dispatch(messagesSlice.actions.setManualInterventionActive(true));
  stopAwaitingAnswer(dispatch);
}

function queueOrStartManualIntervention(
  dispatch: AppDispatch,
  getState: () => RootState,
) {
  if (isAwaitingAnswer(getState)) {
    dispatch(messagesSlice.actions.setManualInterventionPending(true));
    return;
  }

  startManualIntervention(dispatch);
}

/**
 * Ends the awaiting state once the assistant answer is complete: hands over to
 * a queued owner intervention, otherwise re-enables input and hides typing.
 */
function finishAwaitingAnswer(
  dispatch: AppDispatch,
  getState: () => RootState,
) {
  if (getState().messages.manualInterventionPending) {
    startManualIntervention(dispatch);
  } else {
    stopAwaitingAnswer(dispatch);
  }
}

function scheduleTypingIndicatorExpiry(
  dispatch: AppDispatch,
  getState: () => RootState,
  expiryMs?: number | null,
) {
  clearTypingIndicatorExpiryTimer();
  const delay = Math.min(Math.max(Number(expiryMs ?? 5000), 1000), 10_000);
  typingIndicatorExpiryTimer = setTimeout(() => {
    typingIndicatorExpiryTimer = null;
    if (isAwaitingAnswer(getState)) {
      return;
    }
    stopTypingIndicator(dispatch);
  }, delay);
}

export const enum DummyMessageType {
  Visitor,
  Assistant,
  Card,
  Bubble,
  ContactForm,
}

export const getEditorMockMessages = createAppAsyncThunk<
  void,
  DummyMessageType | void
>('messages/getMock', async (dummyMessageType, { dispatch, extra: { t } }) => {
  let messages: WidgetApi.Message[];
  switch (dummyMessageType) {
    case DummyMessageType.Assistant:
      messages = getDummyAssistantMessages(t);
      break;
    case DummyMessageType.Visitor:
      messages = getDummyVisitorMessages(t);
      break;
    case DummyMessageType.Card:
      messages = getDummyCardMessages(t);
      break;
    case DummyMessageType.Bubble:
      messages = getDummyBubbleMessages(t);
      break;
    case DummyMessageType.ContactForm:
      messages = getDummyContactForm(t);
      break;
    default:
      messages = getDummyMessages(t);
      break;
  }

  dispatch(
    messagesSlice.actions.resetMessages({
      messages: formatMessages(messages),
      pagingMetadata: { tooManyToCount: true, cursors: {} },
    }),
  );
});

export const getMessagesHistory = createAppAsyncThunk(
  'messages/get',
  async (
    query: ListMessagesRequest | undefined = messagesQueryBuilder(),
    { dispatch, extra: { httpClient } },
  ) => {
    const resp = await httpClient.request(listMessages(query));
    const { messages, pagingMetadata } = resp.data;

    dispatch(
      messagesSlice.actions.addMessages({
        pagingMetadata,
        messages: formatMessages(messages?.filter(isAllowedMessage)),
      }),
    );
  },
);

export const getConversationId = createAppAsyncThunk(
  'messages/getConversationId',
  async (_, { dispatch, extra: { httpClient, panorama } }) => {
    const panoramaTransaction = panorama.transaction('get-conversation-id');
    panoramaTransaction.start();

    const {
      data: { conversation },
    } = await httpClient.request(getConversation({}));

    panoramaTransaction.finish();

    if (!conversation?.id) {
      const error = new Error('No conversationId in response!');
      panorama.errorMonitor().reportError(error, {
        tags: { feature: 'messages', action: 'getConversationId' },
      });
      throw error;
    }

    dispatch(messagesSlice.actions.setConversationId(conversation!.id!));
    dispatch(flagsSlice.actions.setIsContact(Boolean(conversation!.contact)));

    return conversation!.id;
  },
);

export const subscribeForNewMessages = createAppAsyncThunk(
  'messages/subscribe',
  async (_, { dispatch, getState, extra: { duplexerConnection } }) => {
    if (!duplexerConnection) {
      // No need to listen in Editor
      return;
    }

    /**
     * TODO: set subscription/connection status in store,
     * handle disconnect/errors with relative status, resubscribe
     */

    const conversation =
      selectMessages.conversationId(getState()) ??
      (await dispatch(getConversationId()).unwrap());

    const conversationChannel = createConversationChannel(
      conversation,
      duplexerConnection,
    );

    conversationChannel.onNewMessageEvent((event) =>
      dispatch(newMessageEvent(event)),
    );
    conversationChannel.onNewConversationEvent((event) =>
      dispatch(
        newConversationEvent({
          conversationChannel,
          event,
        }),
      ),
    );

    return () => {
      conversationChannel.close();
    };
  },
);

const newMessageEvent = createAppAsyncThunk(
  'messages/newMessageEvent',
  async (
    event: DuplexerMessage,
    { dispatch, getState, extra: { experiments } },
  ) => {
    if (isOwnerInterventionNotification(event)) {
      queueOrStartManualIntervention(dispatch, getState);
      return;
    }

    if (isNewMessageEvent(event)) {
      if (senderIsOwner(event.sender)) {
        queueOrStartManualIntervention(dispatch, getState);
      }

      if (!isAllowedMessage(event)) {
        return;
      }

      const pendingMessages = selectMessages.pendingMessages(getState());
      if (
        pendingMessages &&
        (senderIsVisitor(event.sender) ||
          // when EXPERIMENTS.STREAM_MESSAGE_RESPONSES is enabled
          // we expect assistant answers from API
          (experiments.enabled(EXPERIMENTS.STREAM_MESSAGE_RESPONSES) &&
            senderIsAssistant(event.sender)) ||
          (event.messageType && SPECIAL_MESSAGE_TYPES.has(event.messageType)))
      ) {
        // It is possible to get event with a message id before we got API response
        // in which case 2 messages are created. This mechanic should solve such cases
        return;
      }

      const message = formatMessage(event);
      dispatch(messagesSlice.actions.addOrUpdateMessage(message));

      const state = getState();
      if (selectLayout.isMinimized(state)) {
        dispatch(messagesSlice.actions.markUnreadFromMessage(message.id));
      }

      if (hasAssistantActions(message)) {
        await dispatch(assistantActions(message.assistantActions));
      }

      // Duplexer typing may set isTyping true while assistantActions runs; clear again
      // so consecutive assistant messages do not leave the indicator stuck.
      if (senderIsAssistant(event.sender)) {
        finishAwaitingAnswer(dispatch, getState);
      }
    } else if (isMessageTypingEvent(event)) {
      const resumedAiTyping = shouldResumeAiTypingAfterManualIntervention(
        event,
        getState,
      );

      if (resumedAiTyping) {
        dispatch(messagesSlice.actions.setManualInterventionActive(false));
        startAwaitingAnswer(dispatch, getState);
      }

      if (!resumedAiTyping && !shouldTrackAwaitingAnswer(getState)) {
        return;
      }

      if (!resumedAiTyping) {
        startTypingIndicator(dispatch);
      }
      scheduleTypingIndicatorExpiry(dispatch, getState, event.expiryMs);
    }
  },
);

const assistantActions = createAppAsyncThunk(
  'messages/assistantActions',
  async (actions: string[], { extra: { siteEcom } }) => {
    return Promise.all(
      actions.map((action) => {
        switch (action) {
          case AssistantAction.AddToCurrentCart:
          case AssistantAction.RemoveLineItemsFromCurrentCart:
          case AssistantAction.UpdateCurrentCart:
          case AssistantAction.UpdateCurrentCartLineItemQuantity:
          case AssistantAction.AddToCart:
          case AssistantAction.RemoveFromCart:
          case AssistantAction.UpdateCart:
          case AssistantAction.RefreshCart:
          case AssistantAction.ClearCart:
            return siteEcom.refreshCart();
          default:
            return Promise.resolve();
        }
      }),
    );
  },
);

const newConversationEvent = createAppAsyncThunk(
  'messages/newConversation',
  async (
    {
      conversationChannel,
      event,
    }: {
      conversationChannel: ConversationChannel;
      event: DuplexerMergeConversationsMessage;
    },
    { dispatch },
  ) => {
    if (event.targetConversationId) {
      conversationChannel.removeAllListeners();
      clearTypingIndicatorExpiryTimer();
      dispatch(
        messagesSlice.actions.resetConversation(event.targetConversationId),
      );
    }
  },
);
export type AddMessagePayload = Partial<Message> & {
  body: { mainText: string };
};

export const enum ActionMessageType {
  Navigation = 'action_navigation',
  AddToCart = 'action_add_to_cart',
  BookService = 'action_book_service',
}

export type AddActionMessagePayload = {
  mainText: string;
  messageType: MessageType;
  data: {
    type: ActionMessageType;
    action: string;
  };
};

/**
 * Sends a single message via the streaming API and dispatches the streamed
 * input/answer updates. Required when EXPERIMENTS.STREAM_MESSAGE_RESPONSES is
 * enabled: assistant answers arrive in this stream, and duplexer assistant
 * events are ignored (see newMessageEvent), so bulkCreateMessages would never
 * surface an answer.
 */
async function streamMessageResponse(
  message: CreateMessageStreamRequest['message'],
  {
    dispatch,
    wixClient,
    panorama,
    optimisticInputMessage,
    renderAnswerChunks = true,
  }: {
    dispatch: AppDispatch;
    wixClient: ThunkExtraArgument['wixClient'];
    panorama: ThunkExtraArgument['panorama'];
    optimisticInputMessage?: Message;
    renderAnswerChunks?: boolean;
  },
) {
  let optimisticAnswer: Message | undefined;
  panorama.logger().info('stream-message-start', {
    messageType: message?.messageType,
  });
  const chunks = WixAssistantWidget.createMessageStream(wixClient)({ message });

  for await (const responses of chunks) {
    const { inputMessage, answerChunks, answerMessage } = responses.reduce<{
      inputMessage: CreateMessageStreamResponse['inputMessage'];
      answerChunks: CreateMessageStreamResponse['answerChunk'][];
      answerMessage: CreateMessageStreamResponse['answerMessage'];
    }>(
      (acc, { inputMessage, answerChunk, answerMessage }) => {
        if (inputMessage) {
          acc.inputMessage = inputMessage;
        }
        if (answerMessage) {
          acc.answerMessage = answerMessage;
        }
        if (answerChunk) {
          acc.answerChunks.push(answerChunk);
        }
        return acc;
      },
      {
        inputMessage: undefined,
        answerChunks: [],
        answerMessage: undefined,
      },
    );
    if (inputMessage) {
      panorama.logger().info('stream-message-input-received', {
        messageType: message?.messageType,
      });
      if (!isAllowedMessage(inputMessage)) {
        continue;
      }
      if (optimisticInputMessage) {
        dispatch(
          messagesSlice.actions.updateMessage({
            id: optimisticInputMessage.id,
            changes: formatMessage(inputMessage),
          }),
        );
      } else {
        dispatch(
          messagesSlice.actions.addOrUpdateMessage(formatMessage(inputMessage)),
        );
      }
    }
    if (answerChunks.length) {
      /**
       * if `prev` is `undefined` this means
       * that this is the first text chunk of the response. Meaning:
       *  - the original message sent is getting an answer (other message types might not be expected to get an answer)
       *  - an `optimisticAnswer` must be displayed now
       **/
      const prev = optimisticAnswer;
      optimisticAnswer = optimisticAnswer ?? {
        id: uuidv4(),
        sender: Sender.ASSISTANT,
        messageType: MessageType.ANSWER,
        createdDate: Date.now(),
        answerTo: optimisticInputMessage?.id,
      };
      const optimisticAnswerId = optimisticAnswer.id;

      if (prev === undefined) {
        panorama.logger().info('stream-message-first-chunk-received', {
          messageType: message?.messageType,
        });
      }

      if (renderAnswerChunks) {
        if (prev === undefined) {
          dispatch(
            messagesSlice.actions.addOptimisticMessage(optimisticAnswer),
          );
        }

        const text = answerChunks.reduce<string>(
          (acc, part) => acc + (part ?? ''),
          '',
        );

        dispatch(
          messagesSlice.actions.appendMessageText({
            id: optimisticAnswerId,
            text,
          }),
        );
      }
    }
    if (answerMessage) {
      panorama.logger().info('stream-message-answer-received', {
        messageType: message?.messageType,
      });

      if (!isAllowedMessage(answerMessage)) {
        continue;
      }

      // if we rendered an `optimisticAnswer` before, we overwrite it with
      // a proper answer
      if (optimisticAnswer) {
        dispatch(
          messagesSlice.actions.updateMessage({
            id: optimisticAnswer.id,
            changes: formatMessage(answerMessage),
          }),
        );
      } else {
        dispatch(
          messagesSlice.actions.addOrUpdateMessage(
            formatMessage(answerMessage),
          ),
        );
      }
    }
  }
}

export const addActionMessage = createAppAsyncThunk(
  'messages/addAction',
  async (
    { messageType, mainText, data: { type, action } }: AddActionMessagePayload,
    {
      dispatch,
      extra: { httpClient, wixClient, panorama, experiments },
      getState,
    },
  ) => {
    if (isAwaitingAnswer(getState)) {
      return;
    }

    const conversationId = selectMessages.conversationId(getState());
    startAwaitingAnswer(dispatch, getState);

    const message = {
      sender: Sender.SITE_VISITOR,
      messageType,
      body: {
        mainText,
        additionalData: [
          {
            id: uuidv4(),
            type,
            action,
          },
        ],
      },
    };

    const isStreaming = experiments.enabled(
      EXPERIMENTS.STREAM_MESSAGE_RESPONSES,
    );
    try {
      if (isStreaming) {
        await streamMessageResponse(message, { dispatch, wixClient, panorama });
        // Duplexer assistant events are ignored while a streamed message is
        // pending (see newMessageEvent), so the stream end is the only
        // reliable signal that the answer is complete.
        finishAwaitingAnswer(dispatch, getState);
      } else {
        await httpClient.request(
          bulkCreateMessages({ messages: [{ ...message, conversationId }] }),
        );
      }
    } catch (error) {
      panorama
        .errorMonitor()
        .reportError(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: {
              feature: 'messages',
              action: 'addActionMessage',
              isStreaming,
            },
          },
        );
      stopAwaitingAnswer(dispatch);
      throw error;
    }
  },
);

export const addMessage = createAppAsyncThunk(
  'messages/add',
  async (
    payload: AddMessagePayload | AddMessagePayload[],
    {
      dispatch,
      extra: { httpClient, wixClient, panorama, experiments },
      getState,
    },
  ) => {
    const conversationId = selectMessages.conversationId(getState());

    const msgs = Array.isArray(payload) ? payload : [payload];
    const hasVisitorPayload = msgs.some((msg) =>
      senderIsVisitor(msg.sender ?? Sender.SITE_VISITOR),
    );
    if (hasVisitorPayload && isAwaitingAnswer(getState)) {
      return;
    }

    const optimisticUpdateMessages: Message[] = msgs.map((msg, index) => ({
      id: uuidv4(),
      sender: Sender.SITE_VISITOR,
      messageType: MessageType.QUESTION,
      conversationId,
      createdDate: Date.now() + index,
      ...msg,
    }));

    let addedVisitorOptimistic = false;
    optimisticUpdateMessages.forEach((msg) => {
      if (isAllowedMessage(msg)) {
        dispatch(messagesSlice.actions.addOptimisticMessage(msg));
        if (senderIsVisitor(msg.sender)) {
          addedVisitorOptimistic = true;
        }
      }
    });

    // Only visitor questions wait for assistant typing; assistant/system addMessage
    // (e.g. CONTACT_FORM) must not turn the indicator back on.
    if (addedVisitorOptimistic) {
      startAwaitingAnswer(dispatch, getState);
    }

    const messages = optimisticUpdateMessages.map(
      ({ body, sender, messageType, answerTo }) => ({
        sender,
        messageType,
        answerTo,
        body,
      }),
    );
    const req = bulkCreateMessages({ messages });

    const isStreaming = experiments.enabled(
      EXPERIMENTS.STREAM_MESSAGE_RESPONSES,
    );
    panorama
      .transaction(PanoramaTransactions.ADD_MESSAGE)
      .start({ isStreaming });
    try {
      if (isStreaming) {
        // Non-visitor messages (INTRO, LEGAL, FAQ_QUESTION, etc.) must not
        // trigger an AI answer — send them via bulkCreateMessages. Only visitor
        // questions go through streamMessageResponse.
        const specialIndices: number[] = [];
        const regularIndices: number[] = [];
        messages.forEach((msg, idx) => {
          if (senderIsVisitor(msg.sender)) {
            regularIndices.push(idx);
          } else {
            specialIndices.push(idx);
          }
        });

        // Stream visitor messages first. Special messages (e.g. CONTACT_FORM) are
        // sent afterward so the server assigns them a later timestamp than the
        // visitor's inputMessage — preventing the CF from sorting before the
        // visitor message due to the sortComparer(createdDate) ordering.
        await Promise.all(
          regularIndices.map((idx) =>
            streamMessageResponse(messages[idx], {
              dispatch,
              wixClient,
              panorama,
              optimisticInputMessage: optimisticUpdateMessages[idx],
            }),
          ),
        );

        if (specialIndices.length > 0) {
          const {
            data: { messages: created },
          } = await httpClient.request(
            bulkCreateMessages({
              messages: specialIndices.map((i) => messages[i]),
            }),
          );
          created?.forEach((m, i) => {
            dispatch(
              messagesSlice.actions.updateMessage({
                id: optimisticUpdateMessages[specialIndices[i]].id,
                changes: formatMessage(m),
              }),
            );
          });
        }
        if (addedVisitorOptimistic) {
          // Duplexer assistant events are ignored while a streamed message is
          // pending (see newMessageEvent), so the stream end is the only
          // reliable signal that the answer is complete.
          finishAwaitingAnswer(dispatch, getState);
        }
        panorama
          .transaction(PanoramaTransactions.ADD_MESSAGE)
          .finish({ isStreaming });
      } else {
        const {
          data: { messages },
          status,
        } = await httpClient.request(req);
        if (status !== 200) {
          // TODO: handle error with UI?
          throw new Error('Failed to add message');
        }

        if (messages?.some((m) => !m.id)) {
          // TODO: should not happen, if yes, fix or make UI
          throw new Error('Some messages were not created');
        }

        panorama
          .transaction(PanoramaTransactions.ADD_MESSAGE)
          .finish({ isStreaming });

        messages?.forEach((m, i) => {
          const optimistic = optimisticUpdateMessages[i];

          dispatch(
            messagesSlice.actions.updateMessage({
              id: optimistic.id,
              changes: formatMessage(m),
            }),
          );
        });
      }
    } catch (error) {
      panorama
        .errorMonitor()
        .reportError(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: { feature: 'messages', action: 'addMessage', isStreaming },
          },
        );
      if (addedVisitorOptimistic) {
        stopAwaitingAnswer(dispatch);
      }
      throw error;
    }
  },
);

export const setRead = createAppAsyncThunk(
  'messages/read',
  async (_, { dispatch }) => {
    dispatch(messagesSlice.actions.setUnread(false));
    dispatch(messagesSlice.actions.clearFirstUnreadMessageId());
  },
);

export const submitVisitorAsContact = createAppAsyncThunk(
  'messages/submitContact',
  async (
    { name, email, phone, message, formId }: FormData,
    { dispatch, getState },
  ) => {
    if (isAwaitingAnswer(getState)) {
      return;
    }

    dispatch(flagsSlice.actions.setIsContact(true));

    dispatch(
      addMessage({
        sender: Sender.SYSTEM,
        messageType: MessageType.CONTACT_FORM_SUBMITTED,
        answerTo: formId,
        body: {
          mainText: message || '',
          additionalData: [
            formSubmittedAdditionalData({
              id: uuidv4(),
              name,
              email,
              phone,
            }),
          ],
        },
      }),
    );
  },
);

export const SELECT_OPTION_ACTION_TYPE = 'select_option';

export const selectOption = createAppAsyncThunk(
  'engineActions/selectOption',
  async (
    { option, answerTo }: { option: string; answerTo: string },
    { dispatch, getState },
  ) => {
    if (isAwaitingAnswer(getState)) {
      return;
    }

    await dispatch(
      addMessage({
        messageType: MessageType.VISITOR_OPTION_SELECTED,
        answerTo,
        body: {
          mainText: option,
          additionalData: [
            {
              id: uuidv4(),
              type: SELECT_OPTION_ACTION_TYPE,
              action: option,
            },
          ],
        },
      }),
    );
  },
);

export const uploadFile = createAppAsyncThunk(
  'messages/uploadFile',
  async (
    { file }: { file: File },
    { dispatch, extra: { httpClient, panorama } },
  ): Promise<FileDescriptor | undefined> => {
    const { name, type } = file;

    try {
      const uploadUrlData = await httpClient.request(
        mediaAttachmentUploadUrl({
          fileName: name,
          mimeType: type,
          parentFolderId: 'visitor-uploads',
        }),
      );

      if (uploadUrlData?.data?.uploadUrl) {
        const params = { filename: name };
        const headers = {
          'Content-Type': type,
        };

        const uploadFileData = await httpClient.put<{ file?: FileDescriptor }>(
          uploadUrlData.data?.uploadUrl,
          file,
          {
            headers,
            params,
          },
        );

        return uploadFileData?.data?.file;
      }

      return undefined;
    } catch (error) {
      panorama
        .errorMonitor()
        .reportError(
          error instanceof Error ? error : new Error(String(error)),
          { tags: { feature: 'messages', action: 'uploadFile' } },
        );
      return undefined;
    }
  },
);

export const addUploadFileMessage = createAppAsyncThunk(
  'messages/addUploadFile',
  async (
    payload: AddMessagePayload,
    {
      dispatch,
      extra: { httpClient, wixClient, panorama, experiments },
      getState,
    },
  ) => {
    if (isAwaitingAnswer(getState)) {
      return;
    }

    const conversationId = selectMessages.conversationId(getState());

    const optimisticUpdateMessage: Message = {
      id: uuidv4(),
      sender: Sender.SITE_VISITOR,
      messageType: MessageType.MEDIA,
      conversationId,
      createdDate: Date.now(),
      ...payload,
    };

    if (isAllowedMessage(optimisticUpdateMessage)) {
      dispatch(
        messagesSlice.actions.addOptimisticMessage(optimisticUpdateMessage),
      );
    }
    startAwaitingAnswer(dispatch, getState);

    const fileName = payload.body.additionalData?.[0]?.file?.name;

    const uploadedFile = await dispatch(
      uploadFile({ file: payload.body.additionalData?.[0]?.file as File }),
    ).unwrap();

    if (!uploadedFile) {
      const { body, ...baseMessage } = optimisticUpdateMessage;
      dispatch(
        messagesSlice.actions.updateMessage({
          id: optimisticUpdateMessage.id,
          changes: {
            ...baseMessage,
            body: {
              mainText: '',
              additionalData: [
                {
                  fileUploadError: 'File upload failed',
                  type: 'file_uploaded',
                  displayName: fileName,
                },
              ],
            },
          },
        }),
      );
      stopAwaitingAnswer(dispatch);
      return;
    }

    const { sender, messageType, answerTo } = optimisticUpdateMessage;
    const message = {
      sender,
      messageType,
      answerTo,
      body: {
        mainText: '',
        additionalData: [{ ...uploadedFile, type: 'file_uploaded' }],
      },
    };

    const isStreaming = experiments.enabled(
      EXPERIMENTS.STREAM_MESSAGE_RESPONSES,
    );
    try {
      if (isStreaming) {
        await streamMessageResponse(message, {
          dispatch,
          wixClient,
          panorama,
          optimisticInputMessage: optimisticUpdateMessage,
        });
        finishAwaitingAnswer(dispatch, getState);
        return;
      }

      const {
        data: { messages },
        status,
      } = await httpClient.request(bulkCreateMessages({ messages: [message] }));
      if (status !== 200 || !messages?.[0] || !messages[0].id) {
        const { body, ...baseMessage } = optimisticUpdateMessage;
        dispatch(
          messagesSlice.actions.updateMessage({
            id: optimisticUpdateMessage.id,
            changes: {
              ...baseMessage,
              body: {
                mainText: '',
                additionalData: [
                  {
                    fileUploadError: 'File upload failed',
                    type: 'file_uploaded',
                    displayName: fileName,
                  },
                ],
              },
            },
          }),
        );
        stopAwaitingAnswer(dispatch);
        return;
      }

      const { body, ...baseMessage } = messages[0] ?? {};
      const updatedMessage = {
        ...baseMessage,
        body: {
          mainText: '',
          additionalData: [{ ...uploadedFile, type: 'file_uploaded' }],
        },
      };
      dispatch(
        messagesSlice.actions.updateMessage({
          id: optimisticUpdateMessage.id,
          changes: formatMessage(updatedMessage),
        }),
      );
    } catch (error) {
      stopAwaitingAnswer(dispatch);
      throw error;
    }
  },
);
